import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Dropdown, { DropdownItem, DropdownList } from '@precis-digital/kurama/Dropdown';
import Grid from '@precis-digital/kurama/Grid';
import SparkleIcon from '@precis-digital/kurama/icons/SparkleIcon';
import { type SlashCommand } from '@precis-digital/kurama/MessageLayout';
import { TooltipTypography } from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { useMemo, useState, type ReactElement } from 'react';
import { type FlowDatasourceEntityType } from 'shared/copilot/flowDataAgentConstants';
import { useTranslation } from 'shared/translations';
import { type Skill } from 'src/copilot/types/skills';
import { getSkillChipColor } from 'src/copilot/utils/mapFlowSkill';
import { getSkillDescriptionTooltipBody, SkillTooltipDescription } from 'shared/copilot/components/SkillTooltipDescription';
import { filterSkillsByQuery, getActiveSkillsForComposer, getMissingPlatformsForSkill, getPlatformDisplayName, getSelectedPlatformsFromEntityTypes, isSkillSelectable } from 'src/copilot/utils/skills';
const preventComposerActionBlur = (event: React.MouseEvent): void => {
  event.preventDefault();
};
interface UnavailableSkillTooltipBodyProps {
  description: string;
  addsKnowledge?: string;
  needsDataSource?: string;
}

/**
 * Flow-style gated skill tooltip: description and optional knowledge first,
 * then a separate emphasized “Needs a … data source” row.
 */
export const UnavailableSkillTooltipBody = t0 => {
  const $ = _c(10);
  const {
    description,
    addsKnowledge,
    needsDataSource
  } = t0;
  let t1;
  if ($[0] !== description) {
    t1 = <SkillTooltipDescription variant="body2">{description}</SkillTooltipDescription>;
    $[0] = description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== addsKnowledge) {
    t2 = addsKnowledge != null && addsKnowledge !== "" ? <TooltipTypography variant="body3">{addsKnowledge}</TooltipTypography> : null;
    $[2] = addsKnowledge;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== needsDataSource) {
    t3 = needsDataSource != null && needsDataSource !== "" ? <Typography variant="h5" color="warningInverse">{needsDataSource}</Typography> : null;
    $[4] = needsDataSource;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <StyledTooltipBody>{t1}{t2}{t3}</StyledTooltipBody>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const buildUnavailableTooltipBody = (skill: Skill, missingPlatforms: string[], labels: {
  addsKnowledge: (knowledge: string) => string;
  needsDataSource: (platform: string) => string;
}): ReactElement => {
  const knowledgeNames = (skill.knowledgeSources ?? []).map(source => source.name).filter(Boolean);
  return <UnavailableSkillTooltipBody description={skill.description} addsKnowledge={knowledgeNames.length > 0 ? labels.addsKnowledge(knowledgeNames.join(', ')) : undefined} needsDataSource={missingPlatforms.length > 0 ? labels.needsDataSource(missingPlatforms.map(getPlatformDisplayName).join(', ')) : undefined} />;
};
export const buildCopilotSkillSlashCommands = (skills: readonly Skill[], selectedPlatforms: ReadonlySet<string>, labels: {
  addsKnowledge: (knowledge: string) => string;
  needsDataSource: (platform: string) => string;
}): SlashCommand[] => {
  return getActiveSkillsForComposer(skills).map(skill => {
    const selectable = isSkillSelectable(skill, selectedPlatforms);
    const missingPlatforms = getMissingPlatformsForSkill(skill, selectedPlatforms);
    const flowName = skill.flowName?.trim();
    const aliases = flowName != null && flowName !== '' && flowName.toLowerCase() !== skill.name.trim().toLowerCase() ? [flowName] : undefined;
    return {
      id: skill.id,
      name: skill.name,
      aliases,
      disabled: !selectable,
      chipColor: getSkillChipColor(skill.scope),
      annotation: missingPlatforms.length > 0 ? labels.needsDataSource(missingPlatforms.map(getPlatformDisplayName).join(', ')) : skill.dataSources[0]?.name,
      leftIcon: <SparkleIcon />,
      tooltip: selectable ? getSkillDescriptionTooltipBody(skill.description) : buildUnavailableTooltipBody(skill, missingPlatforms, labels)
    };
  });
};
interface CopilotComposerSkillsActionProps {
  onSelectSkill: (skill: Skill) => void;
  selectedEntityTypes: readonly FlowDatasourceEntityType[];
  /** Skills from Flow via BFF. Omit or pass `[]` while loading or after a failed fetch. */
  skills?: readonly Skill[];
  /** True while the skills catalog query has not settled (initial load or retry with no data). */
  isLoading?: boolean;
  /**
   * True when the catalog request failed and there is no cached list.
   * Must not be conflated with an empty workspace — that breaks typed `/Name` pinning UX.
   */
  isError?: boolean;
  /** Re-fetch the catalog (e.g. when opening the menu after a failed load). */
  onRetry?: () => void;
}

/**
 * Skills menu control for Copilot Pro `MessageComposer` (`actions` entry).
 * Lists active skills in a searchable flat list; skills that need a missing
 * data source stay visible but are not selectable.
 */
export const CopilotComposerSkillsAction = t0 => {
  const $ = _c(62);
  const {
    onSelectSkill,
    selectedEntityTypes,
    skills,
    isLoading: t1,
    isError: t2,
    onRetry
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isError = t2 === undefined ? false : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "copilot.composer"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const {
    t
  } = useTranslation("common", t3);
  const [skillsAnchorEl, setSkillsAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  let t4;
  if ($[1] !== selectedEntityTypes) {
    t4 = getSelectedPlatformsFromEntityTypes(selectedEntityTypes);
    $[1] = selectedEntityTypes;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const selectedPlatforms = t4;
  let t5;
  if ($[3] !== skills) {
    t5 = skills ?? [];
    $[3] = skills;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = getActiveSkillsForComposer(t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const activeSkills = t6;
  let t7;
  if ($[7] !== activeSkills || $[8] !== searchValue) {
    t7 = filterSkillsByQuery(activeSkills, searchValue);
    $[7] = activeSkills;
    $[8] = searchValue;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const filteredSkills = t7;
  let t8;
  if ($[10] !== t) {
    t8 = {
      addsKnowledge: knowledge => t("skillsAddsKnowledge", {
        knowledge
      }),
      needsDataSource: platform => t("skillsNeedsDataSource", {
        platform
      })
    };
    $[10] = t;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const tooltipLabels = t8;
  let emptyListCopy = null;
  if (!isLoading && filteredSkills.length === 0) {
    if (isError) {
      let t9;
      if ($[12] !== t) {
        t9 = t("skillsLoadError");
        $[12] = t;
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      emptyListCopy = t9;
    } else {
      if (activeSkills.length === 0) {
        let t9;
        if ($[14] !== t) {
          t9 = t("skillsCatalogEmpty");
          $[14] = t;
          $[15] = t9;
        } else {
          t9 = $[15];
        }
        emptyListCopy = t9;
      } else {
        let t9;
        if ($[16] !== t) {
          t9 = t("skillsEmpty");
          $[16] = t;
          $[17] = t9;
        } else {
          t9 = $[17];
        }
        emptyListCopy = t9;
      }
    }
  }
  const t9 = skillsAnchorEl != null;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setSkillsAnchorEl(null);
      setSearchValue("");
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("skillsMenuHeader");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("skillsSearchPlaceholder");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== searchValue || $[24] !== t12) {
    t13 = {
      value: searchValue,
      onChange: setSearchValue,
      placeholder: t12
    };
    $[23] = searchValue;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== emptyListCopy || $[27] !== filteredSkills || $[28] !== onSelectSkill || $[29] !== selectedPlatforms || $[30] !== tooltipLabels) {
    t14 = emptyListCopy != null ? <DropdownItem disabled={true}>{emptyListCopy}</DropdownItem> : filteredSkills.map(skill => {
      const selectable = isSkillSelectable(skill, selectedPlatforms);
      const missingPlatforms = getMissingPlatformsForSkill(skill, selectedPlatforms);
      const tooltipBody = selectable ? getSkillDescriptionTooltipBody(skill.description) : buildUnavailableTooltipBody(skill, missingPlatforms, tooltipLabels);
      return <DropdownItem key={skill.id} leftIcon={<SparkleIcon />} annotation={skill.dataSources.length > 0 ? skill.dataSources.map(_temp).join(", ") : undefined} disabled={!selectable} tooltipProps={tooltipBody != null ? {
        kind: "multiline",
        title: skill.name,
        body: tooltipBody
      } : undefined} onClick={event => {
        event.stopPropagation();
        if (!selectable) {
          return;
        }
        onSelectSkill(skill);
        setSkillsAnchorEl(null);
        setSearchValue("");
      }}>{skill.name}</DropdownItem>;
    });
    $[26] = emptyListCopy;
    $[27] = filteredSkills;
    $[28] = onSelectSkill;
    $[29] = selectedPlatforms;
    $[30] = tooltipLabels;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== isLoading || $[33] !== t11 || $[34] !== t13 || $[35] !== t14) {
    t15 = <DropdownList dense={true} header={t11} minWidth="320px" isLoading={isLoading} searchbar={t13}>{t14}</DropdownList>;
    $[32] = isLoading;
    $[33] = t11;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== skillsAnchorEl || $[38] !== t15 || $[39] !== t9) {
    t16 = <Dropdown anchorEl={skillsAnchorEl} open={t9} onClose={t10}>{t15}</Dropdown>;
    $[37] = skillsAnchorEl;
    $[38] = t15;
    $[39] = t9;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SparkleIcon />;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== isError || $[43] !== onRetry) {
    t18 = event_0 => {
      setSkillsAnchorEl(event_0.currentTarget);
      if (isError) {
        onRetry?.();
      }
    };
    $[42] = isError;
    $[43] = onRetry;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t) {
    t19 = t("skillsButtonTooltip");
    $[45] = t;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t19) {
    t20 = {
      kind: "singleline",
      title: t19
    };
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const t21 = skillsAnchorEl != null;
  let t22;
  if ($[49] !== t) {
    t22 = t("skillsButtonAria");
    $[49] = t;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t) {
    t23 = t("skillsButton");
    $[51] = t;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t18 || $[54] !== t20 || $[55] !== t21 || $[56] !== t22 || $[57] !== t23) {
    t24 = <Button variant="text" scheme="light" leftIcon={t17} onMouseDown={preventComposerActionBlur} onClick={t18} tooltipProps={t20} aria-haspopup="menu" aria-expanded={t21} aria-label={t22}>{t23}</Button>;
    $[53] = t18;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t16 || $[60] !== t24) {
    t25 = <>{t16}{t24}</>;
    $[59] = t16;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  return t25;
};
const StyledTooltipBody = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  width: '100%'
}));
function _temp(dataSource) {
  return dataSource.name;
}